import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, useApiQuery } from '@halo/web-lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Loader2,
  PauseCircle,
  RefreshCw,
  RotateCw,
  Search,
  Settings,
  X,
  AlertTriangle,
} from 'lucide-react';
import { InfoBadge } from '../components/ui/Tooltip';

interface StoreLite {
  id: number;
  name: string;
  type: string;
}

/* ─── Types ───────────────────────────────────── */

interface FailedJobRow {
  id: string;
  queue: string;
  jobName: string;
  bullmqJobId: string;
  payload: Record<string, unknown>;
  lastError: string | null;
  stackTrace: string | null;
  attempts: number;
  storeId: number | null;
  platform: string | null;
  failedAt: string;
  replayedAt: string | null;
  replayedBy: string | null;
  resolved: boolean;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

interface RedisFailedJob {
  jobId: string;
  name: string;
  data?: Record<string, unknown>;
  failedReason?: string;
  attemptsMade?: number;
  failedAt?: string;
  queue?: string;
  storeId?: number | null;
  platform?: string | null;
}

type AnyFailedJob = FailedJobRow | RedisFailedJob;

interface FailedJobsResponse {
  source: 'postgres' | 'redis';
  count: number;
  jobs: AnyFailedJob[];
}

interface ActiveJobRow {
  queue: string;
  jobId: string;
  name: string;
  state: 'active' | 'waiting' | 'delayed';
  storeId: number | null;
  platform: string | null;
  attemptsMade: number;
  enqueuedAt: string | null;
  startedAt: string | null;
  delayUntil: string | null;
  progress: number | object | null;
  data: Record<string, unknown>;
}

interface ActiveJobsResponse {
  count: number;
  jobs: ActiveJobRow[];
}

interface CompletedJobRow {
  queue: string;
  jobId: string;
  name: string;
  storeId: number | null;
  platform: string | null;
  attemptsMade: number;
  enqueuedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  data: Record<string, unknown>;
}

interface CompletedJobsResponse {
  count: number;
  jobs: CompletedJobRow[];
}

type SourceKind = 'postgres' | 'redis' | 'active' | 'completed';

interface Filters {
  platform: string;
  queue: string;
  storeId: string;
  includeResolved: boolean;
}

interface ClientFilters {
  service: string;
  platform: string;
  storeId: string;
  search: string;
}

/* ─── Helpers ─────────────────────────────────── */

function isPostgresRow(j: AnyFailedJob): j is FailedJobRow {
  return 'failedAt' in j && 'jobName' in j;
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function getFailedTimestamp(j: AnyFailedJob): number | null {
  if (isPostgresRow(j)) {
    const t = Date.parse(j.failedAt);
    return Number.isFinite(t) ? t : null;
  }
  if (typeof j.failedAt === 'string' && j.failedAt.length > 0) {
    const t = Date.parse(j.failedAt);
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

function getQueueName(j: AnyFailedJob): string {
  if (isPostgresRow(j)) return j.queue;
  return j.queue ?? '—';
}

function getJobName(j: AnyFailedJob): string {
  if (isPostgresRow(j)) return j.jobName;
  return j.name;
}

function getStoreId(j: AnyFailedJob): number | null {
  if (isPostgresRow(j)) return j.storeId;
  if (typeof j.storeId === 'number') return j.storeId;
  const sid = j.data?.['storeId'];
  if (typeof sid === 'number') return sid;
  if (typeof sid === 'string') {
    const parsed = Number(sid);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function getPlatform(j: AnyFailedJob): string | null {
  if (isPostgresRow(j)) return j.platform;
  if (typeof j.platform === 'string') return j.platform;
  const p = j.data?.['platform'];
  return typeof p === 'string' ? p : null;
}

function getAttempts(j: AnyFailedJob): number {
  if (isPostgresRow(j)) return j.attempts;
  return j.attemptsMade ?? 0;
}

function getErrorMessage(j: AnyFailedJob): string {
  if (isPostgresRow(j)) return j.lastError ?? '';
  return j.failedReason ?? '';
}

function getPayload(j: AnyFailedJob): Record<string, unknown> | null {
  if (isPostgresRow(j)) return j.payload ?? null;
  return j.data ?? null;
}

function getStackTrace(j: AnyFailedJob): string | null {
  if (isPostgresRow(j)) return j.stackTrace;
  return null;
}

function getBullmqJobId(j: AnyFailedJob): string {
  if (isPostgresRow(j)) return j.bullmqJobId;
  return j.jobId;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function getStatus(j: AnyFailedJob): 'open' | 'resolved' | 'replayed' {
  if (!isPostgresRow(j)) return 'open';
  if (j.replayedAt) return 'replayed';
  if (j.resolved) return 'resolved';
  return 'open';
}

function getRowId(j: AnyFailedJob): string {
  if (isPostgresRow(j)) return j.id;
  return `${j.queue ?? 'unknown'}:${j.jobId}`;
}

function getService(j: AnyFailedJob): string {
  // "Service" maps to queue name (e.g. backfill, daily-pull, …)
  return getQueueName(j);
}

function truncate(s: string, len: number): string {
  if (s.length <= len) return s;
  return s.slice(0, len) + '…';
}

/* ─── Sparkline helpers ────────────────────────── */

interface SparklineBucket {
  hourStart: number;
  count: number;
}

function buildHourlyBuckets(jobs: AnyFailedJob[]): SparklineBucket[] {
  const now = Date.now();
  const hourMs = 60 * 60 * 1000;
  const start = Math.floor(now / hourMs) * hourMs - 23 * hourMs;
  const buckets: SparklineBucket[] = [];
  for (let i = 0; i < 24; i++) {
    buckets.push({ hourStart: start + i * hourMs, count: 0 });
  }
  for (const job of jobs) {
    const ts = getFailedTimestamp(job);
    if (ts == null) continue;
    if (ts < start || ts >= start + 24 * hourMs) continue;
    const idx = Math.floor((ts - start) / hourMs);
    if (idx >= 0 && idx < 24) buckets[idx]!.count += 1;
  }
  return buckets;
}

function TrendSparkline({ jobs }: { jobs: AnyFailedJob[] }) {
  const buckets = useMemo(() => buildHourlyBuckets(jobs), [jobs]);
  const total = buckets.reduce((sum, b) => sum + b.count, 0);
  const timestamps = jobs.map((j) => getFailedTimestamp(j)).filter((t): t is number => t != null);
  const span = timestamps.length > 0 ? Math.max(...timestamps) - Math.min(...timestamps) : 0;
  const hourMs = 60 * 60 * 1000;

  if (jobs.length < 24 || span < hourMs) {
    return (
      <span
        className="text-gray-400 dark:text-gray-500 text-xs"
        title="Not enough data for 24h trend"
      >
        —
      </span>
    );
  }

  const width = 120;
  const height = 28;
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const stepX = width / (buckets.length - 1);
  const points = buckets
    .map((b, i) => {
      const x = i * stepX;
      const y = height - (b.count / max) * (height - 2) - 1;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <div
      className="inline-flex items-center gap-2"
      title={`Failures over last 24h (total ${total})`}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className="overflow-visible"
        aria-label="24h failure trend"
      >
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          className="text-red-500 dark:text-red-400"
          points={points}
        />
      </svg>
      <span className="text-[10px] text-gray-400 dark:text-gray-500">24h · {total}</span>
    </div>
  );
}

/* ─── Toast ───────────────────────────────────── */

function ToastBanner({
  message,
  type,
  onDismiss,
}: {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}) {
  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      {type === 'success' ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : (
        <AlertTriangle className="w-4 h-4" />
      )}
      {message}
      <button onClick={onDismiss} className="ml-2 opacity-70 hover:opacity-100">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/* ─── Main Component ──────────────────────────── */

export function FailedJobsPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [source, setSource] = useState<SourceKind>('redis');
  const [filters, setFilters] = useState<Filters>({
    platform: '',
    queue: '',
    storeId: '',
    includeResolved: false,
  });

  // Client-side filters (Service, Platform, Store ID) — persisted in URL.
  const [clientFilters, setClientFilters] = useState<ClientFilters>(() => ({
    service: searchParams.get('service') ?? '',
    platform: searchParams.get('cf_platform') ?? '',
    storeId: searchParams.get('cf_storeId') ?? '',
    search: searchParams.get('q') ?? '',
  }));

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [confirmReplayId, setConfirmReplayId] = useState<string | null>(null);
  const [pauseTarget, setPauseTarget] = useState<{ storeId: number; platform: string } | null>(
    null,
  );
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [bulkBusy, setBulkBusy] = useState<'replay' | 'resolve' | null>(null);

  const { data: storeList } = useApiQuery<StoreLite[]>(
    ['admin', 'failed-jobs', 'stores-lite'],
    '/api/v1/admin/stores',
    {
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const storeMap = useMemo(() => {
    const m = new Map<number, StoreLite>();
    for (const s of storeList ?? []) m.set(s.id, s);
    return m;
  }, [storeList]);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  };

  const handleCopy = async (text: string, label: string) => {
    const ok = await copyToClipboard(text);
    showToast(ok ? `${label} copied` : `Copy failed`, ok ? 'success' : 'error');
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sync URL whenever client filters change.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value: string) => {
      if (value.trim()) next.set(key, value.trim());
      else next.delete(key);
    };
    setOrDelete('service', clientFilters.service);
    setOrDelete('cf_platform', clientFilters.platform);
    setOrDelete('cf_storeId', clientFilters.storeId);
    setOrDelete('q', clientFilters.search);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientFilters]);

  const queryPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('source', source);
    params.set('limit', '200');
    if (filters.platform.trim()) params.set('platform', filters.platform.trim());
    if (filters.queue.trim()) params.set('queue', filters.queue.trim());
    if (filters.storeId.trim()) params.set('storeId', filters.storeId.trim());
    if (filters.includeResolved) params.set('includeResolved', 'true');
    return `/api/v1/ingestion/admin/failed-jobs?${params.toString()}`;
  }, [source, filters]);

  const queryKey = useMemo(
    () => ['admin', 'failed-jobs', source, filters] as const,
    [source, filters],
  );

  const isFailedSource = source === 'postgres' || source === 'redis';

  const { data, isLoading, isFetching, refetch, error } = useApiQuery<FailedJobsResponse>(
    queryKey,
    queryPath,
    {
      refetchInterval: 15_000,
      refetchOnWindowFocus: false,
      enabled: isFailedSource,
    },
  );

  const activeQueryPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '200');
    if (filters.platform.trim()) params.set('platform', filters.platform.trim());
    return `/api/v1/ingestion/admin/active-jobs?${params.toString()}`;
  }, [filters.platform]);

  const {
    data: activeData,
    isLoading: activeLoading,
    isFetching: activeFetching,
    refetch: refetchActive,
    error: activeError,
  } = useApiQuery<ActiveJobsResponse>(
    ['admin', 'active-jobs', filters.platform] as const,
    activeQueryPath,
    {
      refetchInterval: 10_000,
      refetchOnWindowFocus: false,
      enabled: source === 'active',
    },
  );

  const completedQueryPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', '200');
    if (filters.platform.trim()) params.set('platform', filters.platform.trim());
    return `/api/v1/ingestion/admin/completed-jobs?${params.toString()}`;
  }, [filters.platform]);

  const {
    data: completedData,
    isLoading: completedLoading,
    isFetching: completedFetching,
    refetch: refetchCompleted,
    error: completedError,
  } = useApiQuery<CompletedJobsResponse>(
    ['admin', 'completed-jobs', filters.platform] as const,
    completedQueryPath,
    {
      refetchInterval: 30_000,
      refetchOnWindowFocus: false,
      enabled: source === 'completed',
    },
  );

  // For postgres rows we use the durable-ledger endpoints
  // (/failed-jobs/:id/replay|resolve). Redis-only jobs have no ledger row,
  // so we hit the live BullMQ endpoints (/retry-job, /remove-job) keyed by
  // queue + bullmq jobId.
  async function callReplay(job: AnyFailedJob): Promise<void> {
    if (isPostgresRow(job)) {
      const res = await api.post(`/api/v1/ingestion/admin/failed-jobs/${job.id}/replay`);
      if (!res.success) throw new Error(res.error ?? 'Replay failed');
      return;
    }
    const queueName = job.queue;
    if (!queueName) throw new Error('Replay failed: missing queue name');
    const res = await api.post('/api/v1/ingestion/admin/retry-job', {
      queueName,
      jobId: job.jobId,
    });
    if (!res.success) throw new Error(res.error ?? 'Replay failed');
  }

  async function callResolve(job: AnyFailedJob): Promise<void> {
    if (isPostgresRow(job)) {
      const res = await api.post(`/api/v1/ingestion/admin/failed-jobs/${job.id}/resolve`);
      if (!res.success) throw new Error(res.error ?? 'Resolve failed');
      return;
    }
    const queueName = job.queue;
    if (!queueName) throw new Error('Resolve failed: missing queue name');
    const res = await api.post('/api/v1/ingestion/admin/remove-job', {
      queueName,
      jobId: job.jobId,
    });
    if (!res.success) throw new Error(res.error ?? 'Resolve failed');
  }

  const replayMutation = useMutation({
    mutationFn: async (job: AnyFailedJob) => callReplay(job),
    onSuccess: () => {
      setToast({ message: 'Job re-enqueued', type: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'failed-jobs'] });
    },
    onError: (err: Error) => {
      setToast({ message: err.message || 'Replay failed', type: 'error' });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (job: AnyFailedJob) => callResolve(job),
    onSuccess: () => {
      setToast({ message: 'Job marked resolved', type: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'failed-jobs'] });
    },
    onError: (err: Error) => {
      setToast({ message: err.message || 'Resolve failed', type: 'error' });
    },
  });

  const jobs = data?.jobs ?? [];

  // Derive sorted unique service/platform values from current rows.
  const serviceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobs) {
      const v = getService(j);
      if (v && v !== '—') set.add(v);
    }
    return [...set].sort();
  }, [jobs]);

  const platformOptions = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobs) {
      const v = getPlatform(j);
      if (v) set.add(v);
    }
    return [...set].sort();
  }, [jobs]);

  const activeJobs: ActiveJobRow[] = activeData?.jobs ?? [];
  const completedJobs: CompletedJobRow[] = completedData?.jobs ?? [];

  const visibleCompletedJobs = useMemo(() => {
    const storeIdNum = clientFilters.storeId.trim() ? Number(clientFilters.storeId.trim()) : null;
    const q = clientFilters.search.trim().toLowerCase();
    return completedJobs.filter((j) => {
      if (clientFilters.service && j.queue !== clientFilters.service) return false;
      if (clientFilters.platform && j.platform !== clientFilters.platform) return false;
      if (storeIdNum != null && Number.isFinite(storeIdNum)) {
        if (j.storeId !== storeIdNum) return false;
      }
      if (q && !j.name.toLowerCase().includes(q) && !j.queue.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [completedJobs, clientFilters]);

  const visibleActiveJobs = useMemo(() => {
    const storeIdNum = clientFilters.storeId.trim() ? Number(clientFilters.storeId.trim()) : null;
    const q = clientFilters.search.trim().toLowerCase();
    return activeJobs.filter((j) => {
      if (clientFilters.service && j.queue !== clientFilters.service) return false;
      if (clientFilters.platform && j.platform !== clientFilters.platform) return false;
      if (storeIdNum != null && Number.isFinite(storeIdNum)) {
        if (j.storeId !== storeIdNum) return false;
      }
      if (q && !j.name.toLowerCase().includes(q) && !j.queue.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [activeJobs, clientFilters]);

  // Apply client-side filters.
  const visibleJobs = useMemo(() => {
    const storeIdNum = clientFilters.storeId.trim() ? Number(clientFilters.storeId.trim()) : null;
    const q = clientFilters.search.trim().toLowerCase();
    return jobs.filter((j) => {
      if (clientFilters.service && getService(j) !== clientFilters.service) return false;
      if (clientFilters.platform && getPlatform(j) !== clientFilters.platform) return false;
      if (storeIdNum != null && Number.isFinite(storeIdNum)) {
        if (getStoreId(j) !== storeIdNum) return false;
      }
      if (q) {
        const err = getErrorMessage(j).toLowerCase();
        const name = getJobName(j).toLowerCase();
        if (!err.includes(q) && !name.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, clientFilters]);

  // Selectable: any unresolved row. Postgres rows route to the ledger
  // endpoints; redis-only rows route to the live BullMQ endpoints.
  const selectableIds = useMemo(
    () =>
      visibleJobs.filter((j) => (isPostgresRow(j) ? !j.resolved : true)).map((j) => getRowId(j)),
    [visibleJobs],
  );

  // Index by rowId for O(1) lookup when actions run.
  const jobById = useMemo(() => {
    const m = new Map<string, AnyFailedJob>();
    for (const j of visibleJobs) m.set(getRowId(j), j);
    return m;
  }, [visibleJobs]);

  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIds));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (kind: 'replay' | 'resolve') => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const verb = kind === 'replay' ? 'Replay' : 'Resolve';
    if (!window.confirm(`${verb} ${ids.length} selected jobs?`)) return;
    setBulkBusy(kind);
    try {
      const action = kind === 'replay' ? callReplay : callResolve;
      const results = await Promise.all(
        ids.map(async (id) => {
          const job = jobById.get(id);
          if (!job) return { id, ok: false, error: 'Row no longer visible' };
          try {
            await action(job);
            return { id, ok: true, error: null };
          } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed';
            return { id, ok: false, error: message };
          }
        }),
      );
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      if (failCount === 0) {
        setToast({ message: `${verb} succeeded for ${okCount} job(s)`, type: 'success' });
      } else {
        setToast({
          message: `${verb}: ${okCount} ok, ${failCount} failed`,
          type: 'error',
        });
      }
      setSelectedIds(new Set());
      void queryClient.invalidateQueries({ queryKey: ['admin', 'failed-jobs'] });
    } finally {
      setBulkBusy(null);
    }
  };

  return (
    <div>
      {toast && (
        <ToastBanner message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Failed Jobs</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Durable failure ledger from Postgres. Use Replay for transient errors, Resolve for
              issues fixed manually.
            </p>
          </div>
          <TrendSparkline jobs={jobs} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            auto {source === 'active' ? '10s' : source === 'completed' ? '30s' : '15s'}
          </span>
          <button
            onClick={() =>
              void (source === 'active'
                ? refetchActive()
                : source === 'completed'
                  ? refetchCompleted()
                  : refetch())
            }
            disabled={
              source === 'active'
                ? activeFetching
                : source === 'completed'
                  ? completedFetching
                  : isFetching
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
          >
            {(
              source === 'active'
                ? activeFetching
                : source === 'completed'
                  ? completedFetching
                  : isFetching
            ) ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Source pills + search */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <SourcePill
          label="postgres"
          active={source === 'postgres'}
          onClick={() => setSource('postgres')}
          tip="Durable failed-job ledger persisted in Postgres."
        />
        <SourcePill
          label="redis (live)"
          active={source === 'redis'}
          onClick={() => setSource('redis')}
          tip="Live failed jobs from BullMQ in Redis. Volatile; does not show resolved/replayed state."
        />
        <SourcePill
          label="active (live)"
          active={source === 'active'}
          onClick={() => setSource('active')}
          tip="Jobs currently active, waiting, or delayed across all BullMQ queues."
        />
        <SourcePill
          label="completed (live)"
          active={source === 'completed'}
          onClick={() => setSource('completed')}
          tip="Recently completed BullMQ jobs across all queues. Retention depends on each queue's keep-completed config."
        />
        <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-1">
          {source === 'active'
            ? activeData
              ? `${visibleActiveJobs.length} of ${activeData.count} job${activeData.count === 1 ? '' : 's'}`
              : ''
            : source === 'completed'
              ? completedData
                ? `${visibleCompletedJobs.length} of ${completedData.count} job${completedData.count === 1 ? '' : 's'}`
                : ''
              : data
                ? `${visibleJobs.length} of ${data.count} job${data.count === 1 ? '' : 's'}`
                : ''}
        </span>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={clientFilters.search}
            onChange={(e) => setClientFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder={isFailedSource ? 'Search error or job name…' : 'Search job name…'}
            className="w-full pl-8 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300 placeholder:text-gray-400"
          />
          {clientFilters.search && (
            <button
              onClick={() => setClientFilters((f) => ({ ...f, search: '' }))}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Server-side filters */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <FilterField label="Platform (server)">
            <input
              type="text"
              value={filters.platform}
              onChange={(e) => setFilters((f) => ({ ...f, platform: e.target.value }))}
              placeholder="shopify, meta, …"
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300 placeholder:text-gray-300"
            />
          </FilterField>
          <FilterField label="Queue (server)">
            <input
              type="text"
              value={filters.queue}
              onChange={(e) => setFilters((f) => ({ ...f, queue: e.target.value }))}
              placeholder="backfill, daily-pull, …"
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300 placeholder:text-gray-300"
            />
          </FilterField>
          <FilterField label="Store ID (server)">
            <input
              type="number"
              value={filters.storeId}
              onChange={(e) => setFilters((f) => ({ ...f, storeId: e.target.value }))}
              placeholder="e.g. 42"
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300 placeholder:text-gray-300"
            />
          </FilterField>
          <FilterField label="Include resolved">
            <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200 cursor-pointer h-[30px]">
              <input
                type="checkbox"
                checked={filters.includeResolved}
                onChange={(e) => setFilters((f) => ({ ...f, includeResolved: e.target.checked }))}
                disabled={source !== 'postgres'}
                className="rounded border-gray-300 dark:border-gray-700 text-blue-600 focus:ring-blue-300 disabled:opacity-50"
              />
              <span className={source !== 'postgres' ? 'opacity-50' : ''}>Show resolved</span>
            </label>
          </FilterField>
        </div>
      </div>

      {/* Client-side filters (URL-persisted) */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-3 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FilterField label="Service">
            <select
              value={clientFilters.service}
              onChange={(e) => setClientFilters((f) => ({ ...f, service: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300"
            >
              <option value="">All services</option>
              {serviceOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Platform">
            <select
              value={clientFilters.platform}
              onChange={(e) => setClientFilters((f) => ({ ...f, platform: e.target.value }))}
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300"
            >
              <option value="">All platforms</option>
              {platformOptions.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </FilterField>
          <FilterField label="Store ID">
            <input
              type="number"
              value={clientFilters.storeId}
              onChange={(e) => setClientFilters((f) => ({ ...f, storeId: e.target.value }))}
              placeholder="e.g. 42"
              className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-300 placeholder:text-gray-300"
            />
          </FilterField>
        </div>
      </div>

      {/* Bulk action toolbar (failed sources only) */}
      {isFailedSource && (
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            {someSelected ? `${selectedIds.size} selected` : 'Select rows to enable bulk actions'}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void runBulk('replay')}
              disabled={!someSelected || bulkBusy !== null}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {bulkBusy === 'replay' ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RotateCw className="w-3.5 h-3.5" />
              )}
              Replay selected ({selectedIds.size})
            </button>
            <button
              onClick={() => void runBulk('resolve')}
              disabled={!someSelected || bulkBusy !== null}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {bulkBusy === 'resolve' ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5" />
              )}
              Resolve selected ({selectedIds.size})
            </button>
          </div>
        </div>
      )}

      {/* Active jobs table */}
      {source === 'active' && (
        <ActiveJobsTable
          jobs={visibleActiveJobs}
          totalCount={activeData?.count ?? 0}
          isLoading={activeLoading}
          error={activeError}
          storeMap={storeMap}
          onFilterStore={(id) => setClientFilters((f) => ({ ...f, storeId: String(id) }))}
          onFilterPlatform={(p) => setClientFilters((f) => ({ ...f, platform: p }))}
          onFilterQueue={(q) => setClientFilters((f) => ({ ...f, service: q }))}
        />
      )}

      {/* Completed jobs table */}
      {source === 'completed' && (
        <CompletedJobsTable
          jobs={visibleCompletedJobs}
          totalCount={completedData?.count ?? 0}
          isLoading={completedLoading}
          error={completedError}
          storeMap={storeMap}
          onFilterStore={(id) => setClientFilters((f) => ({ ...f, storeId: String(id) }))}
          onFilterPlatform={(p) => setClientFilters((f) => ({ ...f, platform: p }))}
          onFilterQueue={(q) => setClientFilters((f) => ({ ...f, service: q }))}
        />
      )}

      {/* Failed jobs table */}
      {isFailedSource && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 mr-2" />
              {error.message}
            </div>
          ) : visibleJobs.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-400 dark:text-gray-500">
              No failed jobs.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400">
                  <tr>
                    <Th className="w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        disabled={selectableIds.length === 0}
                        aria-label="Select all"
                        className="rounded border-gray-300 dark:border-gray-700 text-blue-600 focus:ring-blue-300 disabled:opacity-50"
                      />
                    </Th>
                    <Th className="w-6" />
                    <Th>Failed at</Th>
                    <Th>Queue / Job</Th>
                    <Th>Store</Th>
                    <Th>Platform</Th>
                    <Th>Attempts</Th>
                    <Th>Error</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {visibleJobs.map((job) => {
                    const id = getRowId(job);
                    const ts = getFailedTimestamp(job);
                    const status = getStatus(job);
                    const isPg = isPostgresRow(job);
                    const isResolved = isPg && job.resolved;
                    const errMsg = getErrorMessage(job);
                    const storeId = getStoreId(job);
                    const platform = getPlatform(job);
                    const queueName = getQueueName(job);
                    const store = storeId != null ? storeMap.get(storeId) : undefined;
                    const expanded = expandedIds.has(id);
                    const payload = getPayload(job);
                    const stack = getStackTrace(job);
                    const replayBusy =
                      replayMutation.isPending &&
                      replayMutation.variables !== undefined &&
                      getRowId(replayMutation.variables) === id;
                    const resolveBusy =
                      resolveMutation.isPending &&
                      resolveMutation.variables !== undefined &&
                      getRowId(resolveMutation.variables) === id;
                    const selectable = !isResolved;
                    const resolveLabel = isPg ? 'Resolve' : 'Remove';
                    const resolveTitle = isPg
                      ? 'Mark resolved without replaying'
                      : 'Remove this job from the failed set without retrying';

                    return (
                      <Fragment key={id}>
                        <tr className="text-gray-700 dark:text-gray-200">
                          <Td>
                            <input
                              type="checkbox"
                              checked={selectedIds.has(id)}
                              onChange={() => toggleOne(id)}
                              disabled={!selectable}
                              aria-label={`Select ${id}`}
                              className="rounded border-gray-300 dark:border-gray-700 text-blue-600 focus:ring-blue-300 disabled:opacity-30"
                            />
                          </Td>
                          <Td>
                            <button
                              onClick={() => toggleExpanded(id)}
                              aria-label={expanded ? 'Collapse row' : 'Expand row'}
                              className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              {expanded ? (
                                <ChevronDown className="w-3.5 h-3.5" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </Td>
                          <Td>
                            {ts ? (
                              <span title={new Date(ts).toLocaleString()}>{timeAgo(ts)}</span>
                            ) : (
                              '—'
                            )}
                          </Td>
                          <Td>
                            <button
                              type="button"
                              onClick={() =>
                                setClientFilters((f) => ({ ...f, service: queueName }))
                              }
                              title={`Filter by queue: ${queueName}`}
                              className="font-medium text-gray-900 dark:text-gray-100 hover:underline text-left"
                            >
                              {queueName}
                            </button>
                            <div className="text-[10px] text-gray-400 dark:text-gray-500">
                              {getJobName(job)}
                            </div>
                          </Td>
                          <Td>
                            {storeId != null ? (
                              <div className="flex flex-col">
                                <div className="inline-flex items-center gap-1">
                                  <Link
                                    to={`/stores/${storeId}`}
                                    className="font-medium text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[180px]"
                                    title={store?.name ?? `Store #${storeId}`}
                                  >
                                    {store?.name ?? `Store #${storeId}`}
                                  </Link>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setClientFilters((f) => ({
                                        ...f,
                                        storeId: String(storeId),
                                      }))
                                    }
                                    title="Filter by this store"
                                    className="text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                                  >
                                    <span className="text-[10px]">▾</span>
                                  </button>
                                </div>
                                <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                  id: {storeId}
                                  {store?.type ? ` · ${store.type}` : ''}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </Td>
                          <Td>
                            {platform ? (
                              <button
                                type="button"
                                onClick={() => setClientFilters((f) => ({ ...f, platform }))}
                                title={`Filter by platform: ${platform}`}
                                className="text-gray-700 dark:text-gray-200 hover:underline"
                              >
                                {platform}
                              </button>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </Td>
                          <Td>{getAttempts(job)}</Td>
                          <Td>
                            {errMsg ? (
                              <div className="inline-flex items-start gap-1.5 group">
                                <InfoBadge
                                  tip={
                                    <span className="whitespace-pre-wrap break-words">
                                      {errMsg}
                                    </span>
                                  }
                                >
                                  <span className="text-red-600 dark:text-red-400">
                                    {truncate(errMsg, 80)}
                                  </span>
                                </InfoBadge>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void handleCopy(errMsg, 'Error');
                                  }}
                                  title="Copy error"
                                  aria-label="Copy error"
                                  className="shrink-0 p-0.5 rounded text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </Td>
                          <Td>
                            <StatusBadge status={status} source={source} />
                          </Td>
                          <Td className="text-right">
                            <div className="inline-flex items-center gap-1.5">
                              {storeId != null && (
                                <Link
                                  to={`/ingestion/${storeId}`}
                                  title="Open ingestion configs for this store"
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-50 dark:hover:bg-gray-950"
                                >
                                  <Settings className="w-3 h-3" />
                                  Configs
                                </Link>
                              )}
                              {storeId != null && platform && (
                                <button
                                  onClick={() => setPauseTarget({ storeId, platform })}
                                  disabled={isResolved}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium border border-amber-300 text-amber-700 dark:border-amber-500/40 dark:text-amber-400 rounded hover:bg-amber-50 dark:hover:bg-amber-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                                  title="Pause this integration so no more sync jobs are enqueued"
                                >
                                  <PauseCircle className="w-3 h-3" />
                                  Pause integration
                                </button>
                              )}
                              <button
                                onClick={() => setConfirmReplayId(id)}
                                disabled={isResolved || replayBusy || resolveBusy}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Re-enqueue this job"
                              >
                                {replayBusy ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RotateCw className="w-3 h-3" />
                                )}
                                Replay
                              </button>
                              <button
                                onClick={() => resolveMutation.mutate(job)}
                                disabled={isResolved || replayBusy || resolveBusy}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-40 disabled:cursor-not-allowed"
                                title={resolveTitle}
                              >
                                {resolveBusy ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CheckCircle2 className="w-3 h-3" />
                                )}
                                {resolveLabel}
                              </button>
                            </div>
                          </Td>
                        </tr>
                        {expanded && (
                          <tr className="bg-gray-50/60 dark:bg-gray-950/40">
                            <td colSpan={10} className="px-3 py-3">
                              <ExpandedDetails
                                jobId={id}
                                bullmqJobId={getBullmqJobId(job)}
                                storeId={storeId}
                                storeName={store?.name ?? null}
                                payload={payload}
                                stackTrace={stack}
                                errorMessage={errMsg}
                                onCopy={handleCopy}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Pause integration dialog */}
      {pauseTarget && (
        <PauseIntegrationDialog
          storeId={pauseTarget.storeId}
          platform={pauseTarget.platform}
          onClose={() => setPauseTarget(null)}
          onSuccess={(platform) => {
            setPauseTarget(null);
            setToast({ message: `${platform} integration paused`, type: 'success' });
            void queryClient.invalidateQueries({ queryKey: ['admin', 'failed-jobs'] });
          }}
          onError={(message) => setToast({ message, type: 'error' })}
        />
      )}

      {/* Replay confirm dialog */}
      {confirmReplayId && (
        <ConfirmReplayDialog
          jobId={confirmReplayId}
          onCancel={() => setConfirmReplayId(null)}
          onConfirm={() => {
            const id = confirmReplayId;
            setConfirmReplayId(null);
            const job = jobById.get(id);
            if (job) replayMutation.mutate(job);
          }}
        />
      )}
    </div>
  );
}

/* ─── Small subcomponents ─────────────────────── */

function ActiveJobsTable({
  jobs,
  totalCount,
  isLoading,
  error,
  storeMap,
  onFilterStore,
  onFilterPlatform,
  onFilterQueue,
}: {
  jobs: ActiveJobRow[];
  totalCount: number;
  isLoading: boolean;
  error: { message: string } | null;
  storeMap: Map<number, StoreLite>;
  onFilterStore: (storeId: number) => void;
  onFilterPlatform: (platform: string) => void;
  onFilterQueue: (queue: string) => void;
}) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error.message}
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-400 dark:text-gray-500">
          {totalCount === 0 ? 'No active or waiting jobs.' : 'No jobs match the current filters.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400">
              <tr>
                <Th>State</Th>
                <Th>Queue / Job</Th>
                <Th>Store</Th>
                <Th>Platform</Th>
                <Th>Started / Enqueued</Th>
                <Th>Progress</Th>
                <Th>Attempts</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {jobs.map((j) => {
                const store = j.storeId != null ? storeMap.get(j.storeId) : undefined;
                const tsString =
                  j.state === 'active'
                    ? j.startedAt
                    : j.state === 'delayed'
                      ? j.delayUntil
                      : j.enqueuedAt;
                const ts = tsString ? Date.parse(tsString) : null;
                const progressLabel =
                  typeof j.progress === 'number'
                    ? `${j.progress}%`
                    : j.progress && typeof j.progress === 'object'
                      ? JSON.stringify(j.progress)
                      : '—';
                return (
                  <tr key={`${j.queue}:${j.jobId}`} className="text-gray-700 dark:text-gray-200">
                    <Td>
                      <ActiveStateBadge state={j.state} />
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => onFilterQueue(j.queue)}
                        title={`Filter by queue: ${j.queue}`}
                        className="font-medium text-gray-900 dark:text-gray-100 hover:underline text-left"
                      >
                        {j.queue}
                      </button>
                      <div className="text-[10px] text-gray-400 dark:text-gray-500">{j.name}</div>
                    </Td>
                    <Td>
                      {j.storeId != null ? (
                        <div className="flex flex-col">
                          <div className="inline-flex items-center gap-1">
                            <Link
                              to={`/stores/${j.storeId}`}
                              className="font-medium text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[180px]"
                              title={store?.name ?? `Store #${j.storeId}`}
                            >
                              {store?.name ?? `Store #${j.storeId}`}
                            </Link>
                            <button
                              type="button"
                              onClick={() => onFilterStore(j.storeId as number)}
                              title="Filter by this store"
                              className="text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                            >
                              <span className="text-[10px]">▾</span>
                            </button>
                          </div>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            id: {j.storeId}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </Td>
                    <Td>
                      {j.platform ? (
                        <button
                          type="button"
                          onClick={() => onFilterPlatform(j.platform!)}
                          title={`Filter by platform: ${j.platform}`}
                          className="text-gray-700 dark:text-gray-200 hover:underline"
                        >
                          {j.platform}
                        </button>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </Td>
                    <Td>
                      {ts ? <span title={new Date(ts).toLocaleString()}>{timeAgo(ts)}</span> : '—'}
                    </Td>
                    <Td>{progressLabel}</Td>
                    <Td>{j.attemptsMade}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const secs = ms / 1000;
  if (secs < 60) return `${secs.toFixed(secs < 10 ? 1 : 0)}s`;
  const mins = Math.floor(secs / 60);
  const remSecs = Math.floor(secs % 60);
  if (mins < 60) return remSecs > 0 ? `${mins}m ${remSecs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins > 0 ? `${hrs}h ${remMins}m` : `${hrs}h`;
}

function CompletedJobsTable({
  jobs,
  totalCount,
  isLoading,
  error,
  storeMap,
  onFilterStore,
  onFilterPlatform,
  onFilterQueue,
}: {
  jobs: CompletedJobRow[];
  totalCount: number;
  isLoading: boolean;
  error: { message: string } | null;
  storeMap: Map<number, StoreLite>;
  onFilterStore: (storeId: number) => void;
  onFilterPlatform: (platform: string) => void;
  onFilterQueue: (queue: string) => void;
}) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error.message}
        </div>
      ) : jobs.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-sm text-gray-400 dark:text-gray-500">
          {totalCount === 0
            ? 'No completed jobs in the recent window.'
            : 'No jobs match the current filters.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400">
              <tr>
                <Th>Finished</Th>
                <Th>Queue / Job</Th>
                <Th>Store</Th>
                <Th>Platform</Th>
                <Th>Duration</Th>
                <Th>Attempts</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {jobs.map((j) => {
                const store = j.storeId != null ? storeMap.get(j.storeId) : undefined;
                const finishedTs = j.finishedAt ? Date.parse(j.finishedAt) : null;
                return (
                  <tr key={`${j.queue}:${j.jobId}`} className="text-gray-700 dark:text-gray-200">
                    <Td>
                      {finishedTs ? (
                        <span title={new Date(finishedTs).toLocaleString()}>
                          {timeAgo(finishedTs)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td>
                      <button
                        type="button"
                        onClick={() => onFilterQueue(j.queue)}
                        title={`Filter by queue: ${j.queue}`}
                        className="font-medium text-gray-900 dark:text-gray-100 hover:underline text-left"
                      >
                        {j.queue}
                      </button>
                      <div className="text-[10px] text-gray-400 dark:text-gray-500">{j.name}</div>
                    </Td>
                    <Td>
                      {j.storeId != null ? (
                        <div className="flex flex-col">
                          <div className="inline-flex items-center gap-1">
                            <Link
                              to={`/stores/${j.storeId}`}
                              className="font-medium text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[180px]"
                              title={store?.name ?? `Store #${j.storeId}`}
                            >
                              {store?.name ?? `Store #${j.storeId}`}
                            </Link>
                            <button
                              type="button"
                              onClick={() => onFilterStore(j.storeId as number)}
                              title="Filter by this store"
                              className="text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                            >
                              <span className="text-[10px]">▾</span>
                            </button>
                          </div>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            id: {j.storeId}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </Td>
                    <Td>
                      {j.platform ? (
                        <button
                          type="button"
                          onClick={() => onFilterPlatform(j.platform!)}
                          title={`Filter by platform: ${j.platform}`}
                          className="text-gray-700 dark:text-gray-200 hover:underline"
                        >
                          {j.platform}
                        </button>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">—</span>
                      )}
                    </Td>
                    <Td>{formatDuration(j.durationMs)}</Td>
                    <Td>{j.attemptsMade}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ActiveStateBadge({ state }: { state: 'active' | 'waiting' | 'delayed' }) {
  if (state === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
        active
      </span>
    );
  }
  if (state === 'delayed') {
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400">
        delayed
      </span>
    );
  }
  return (
    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400">
      waiting
    </span>
  );
}

function SourcePill({
  label,
  active,
  onClick,
  tip,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tip: string;
}) {
  return (
    <InfoBadge tip={tip}>
      <button
        onClick={onClick}
        className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
          active
            ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 border-gray-900 dark:border-gray-100'
            : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-950'
        }`}
      >
        {label}
      </button>
    </InfoBadge>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`text-left font-medium px-3 py-2 text-[10px] uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

function StatusBadge({
  status,
  source,
}: {
  status: 'open' | 'resolved' | 'replayed';
  source: SourceKind;
}) {
  if (source === 'redis') {
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400">
        live
      </span>
    );
  }
  if (status === 'replayed') {
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-400">
        replayed
      </span>
    );
  }
  if (status === 'resolved') {
    return (
      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400">
        resolved
      </span>
    );
  }
  return (
    <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400">
      open
    </span>
  );
}

function ExpandedDetails({
  jobId,
  bullmqJobId,
  storeId,
  storeName,
  payload,
  stackTrace,
  errorMessage,
  onCopy,
}: {
  jobId: string;
  bullmqJobId: string;
  storeId: number | null;
  storeName: string | null;
  payload: Record<string, unknown> | null;
  stackTrace: string | null;
  errorMessage: string;
  onCopy: (text: string, label: string) => void;
}) {
  const payloadJson = payload ? JSON.stringify(payload, null, 2) : null;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-gray-500 dark:text-gray-400">Row ID:</span>
        <code className="font-mono text-gray-700 dark:text-gray-200 break-all">{jobId}</code>
        <button
          onClick={() => onCopy(jobId, 'Row ID')}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-900"
          title="Copy row id"
        >
          <Copy className="w-3 h-3" />
        </button>
        <span className="text-gray-300 dark:text-gray-700">|</span>
        <span className="text-gray-500 dark:text-gray-400">BullMQ Job ID:</span>
        <code className="font-mono text-gray-700 dark:text-gray-200 break-all">{bullmqJobId}</code>
        <button
          onClick={() => onCopy(bullmqJobId, 'Job ID')}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-900"
          title="Copy BullMQ job id"
        >
          <Copy className="w-3 h-3" />
        </button>
        {storeId != null && (
          <>
            <span className="text-gray-300 dark:text-gray-700">|</span>
            <Link
              to={`/stores/${storeId}`}
              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              {storeName ?? `Store #${storeId}`}
            </Link>
            <Link
              to={`/ingestion/${storeId}`}
              className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Settings className="w-3 h-3" />
              Ingestion configs
            </Link>
          </>
        )}
      </div>

      {errorMessage && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-[10px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
              Error
            </h4>
            <button
              onClick={() => onCopy(errorMessage, 'Error')}
              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              title="Copy error message"
            >
              <Copy className="w-3 h-3" />
              Copy
            </button>
          </div>
          <pre className="text-[11px] bg-white dark:bg-gray-900 border border-red-100 dark:border-red-900/40 rounded-lg p-2 text-red-700 dark:text-red-400 whitespace-pre-wrap break-words max-h-40 overflow-auto">
            {errorMessage}
          </pre>
        </div>
      )}

      {payloadJson && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-[10px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
              Payload
            </h4>
            <button
              onClick={() => onCopy(payloadJson, 'Payload')}
              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              title="Copy payload JSON"
            >
              <Copy className="w-3 h-3" />
              Copy JSON
            </button>
          </div>
          <pre className="text-[11px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-2 text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words max-h-60 overflow-auto font-mono">
            {payloadJson}
          </pre>
        </div>
      )}

      {stackTrace && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-[10px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400">
              Stack trace
            </h4>
            <button
              onClick={() => onCopy(stackTrace, 'Stack trace')}
              className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-200"
              title="Copy stack trace"
            >
              <Copy className="w-3 h-3" />
              Copy
            </button>
          </div>
          <pre className="text-[11px] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-2 text-gray-600 dark:text-gray-400 whitespace-pre-wrap break-words max-h-60 overflow-auto font-mono">
            {stackTrace}
          </pre>
        </div>
      )}
    </div>
  );
}

function ConfirmReplayDialog({
  jobId,
  onCancel,
  onConfirm,
}: {
  jobId: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-md w-full mx-4 p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center shrink-0">
            <RotateCw className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Replay this job?
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              The original payload will be re-enqueued onto its queue and the failure row will be
              marked replayed + resolved.
            </p>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 font-mono break-all">
              id: {jobId}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Replay
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Pause integration dialog ──────────────── */

const PAUSE_REASON_CODES = [
  'REAUTH_REQUIRED_GOOGLE',
  'REAUTH_REQUIRED_GOOGLE_ADS',
  'REAUTH_REQUIRED_GA4',
  'REAUTH_REQUIRED_META',
  'REAUTH_REQUIRED_SHOPIFY',
  'REAUTH_REQUIRED_TIKTOK',
  'REAUTH_REQUIRED_PINTEREST',
  'REAUTH_REQUIRED_AMAZON',
  'REAUTH_REQUIRED_KLAVIYO',
  'PERMISSIONS_CHANGED',
  'ACCOUNT_DISCONNECTED_UPSTREAM',
  'BILLING_ISSUE_UPSTREAM',
  'OTHER',
] as const;

type PauseReasonCode = (typeof PAUSE_REASON_CODES)[number];

const DEFAULT_PAUSE_MESSAGES: Record<PauseReasonCode, string> = {
  REAUTH_REQUIRED_GOOGLE:
    'Your Google connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_GOOGLE_ADS:
    'Your Google Ads connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_GA4:
    'Your Google Analytics connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_META:
    'Your Meta (Facebook/Instagram) connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_SHOPIFY:
    'Your Shopify connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_TIKTOK:
    'Your TikTok connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_PINTEREST:
    'Your Pinterest connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_AMAZON:
    'Your Amazon connection needs to be reauthorized. Please reconnect from the Integrations page.',
  REAUTH_REQUIRED_KLAVIYO:
    'Your Klaviyo connection needs to be reauthorized. Please reconnect from the Integrations page.',
  PERMISSIONS_CHANGED:
    'Required permissions are missing on your account. Please reconnect and grant all requested scopes.',
  ACCOUNT_DISCONNECTED_UPSTREAM:
    "The upstream account appears disconnected on the provider's side. Please reconnect to resume syncing.",
  BILLING_ISSUE_UPSTREAM:
    'Your ad account has been suspended by the provider, usually for a billing issue. Resolve it with the provider, then contact support to resume syncing.',
  OTHER: '',
};

const REASON_CODE_LABELS: Record<PauseReasonCode, string> = {
  REAUTH_REQUIRED_GOOGLE: 'Reauth required — Google',
  REAUTH_REQUIRED_GOOGLE_ADS: 'Reauth required — Google Ads',
  REAUTH_REQUIRED_GA4: 'Reauth required — GA4',
  REAUTH_REQUIRED_META: 'Reauth required — Meta',
  REAUTH_REQUIRED_SHOPIFY: 'Reauth required — Shopify',
  REAUTH_REQUIRED_TIKTOK: 'Reauth required — TikTok',
  REAUTH_REQUIRED_PINTEREST: 'Reauth required — Pinterest',
  REAUTH_REQUIRED_AMAZON: 'Reauth required — Amazon',
  REAUTH_REQUIRED_KLAVIYO: 'Reauth required — Klaviyo',
  PERMISSIONS_CHANGED: 'Permissions changed',
  ACCOUNT_DISCONNECTED_UPSTREAM: 'Account disconnected upstream',
  BILLING_ISSUE_UPSTREAM: 'Billing issue upstream',
  OTHER: 'Other (custom message)',
};

interface ConnectionSummary {
  id: string;
  platform: string;
  pausedAt: string | null;
}

function suggestReasonForPlatform(platform: string): PauseReasonCode {
  switch (platform) {
    case 'google_ads':
      return 'REAUTH_REQUIRED_GOOGLE_ADS';
    case 'ga4':
      return 'REAUTH_REQUIRED_GA4';
    case 'meta':
      return 'REAUTH_REQUIRED_META';
    case 'shopify':
      return 'REAUTH_REQUIRED_SHOPIFY';
    case 'tiktok_business':
    case 'tiktok_shop':
      return 'REAUTH_REQUIRED_TIKTOK';
    case 'pinterest':
      return 'REAUTH_REQUIRED_PINTEREST';
    case 'amazon_seller':
    case 'amazon_ads':
      return 'REAUTH_REQUIRED_AMAZON';
    case 'klaviyo':
      return 'REAUTH_REQUIRED_KLAVIYO';
    default:
      return 'REAUTH_REQUIRED_GOOGLE';
  }
}

function PauseIntegrationDialog({
  storeId,
  platform,
  onClose,
  onSuccess,
  onError,
}: {
  storeId: number;
  platform: string;
  onClose: () => void;
  onSuccess: (platform: string) => void;
  onError: (message: string) => void;
}) {
  const initialReason = suggestReasonForPlatform(platform);
  const [reasonCode, setReasonCode] = useState<PauseReasonCode>(initialReason);
  const [message, setMessage] = useState<string>(DEFAULT_PAUSE_MESSAGES[initialReason]);
  const [internalNote, setInternalNote] = useState<string>('');
  const [messageEdited, setMessageEdited] = useState(false);

  const {
    data: connections,
    isLoading: lookupLoading,
    error: lookupError,
  } = useApiQuery<ConnectionSummary[]>(
    ['admin', 'connection-lookup', storeId] as const,
    `/api/v1/admin/stores/${storeId}/connections`,
  );
  const connection = connections?.find((c) => c.platform === platform) ?? null;

  const pauseMutation = useMutation({
    mutationFn: async () => {
      if (!connection) throw new Error('Connection not found for this store + platform');
      const trimmed = message.trim();
      if (reasonCode === 'OTHER' && trimmed.length === 0) {
        throw new Error('A custom message is required when reason is "Other"');
      }
      const body: Record<string, unknown> = { reasonCode };
      if (trimmed.length > 0) body['message'] = trimmed;
      if (internalNote.trim().length > 0) body['internalNote'] = internalNote.trim();
      const res = await api.post(`/api/v1/admin/connections/${connection.id}/pause`, body);
      if (!res.success) throw new Error(res.error ?? 'Pause failed');
    },
    onSuccess: () => onSuccess(platform),
    onError: (err: Error) => onError(err.message || 'Pause failed'),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <PauseCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Pause integration
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Store <span className="font-mono">{storeId}</span> &middot; Platform{' '}
            <span className="font-mono">{platform}</span>
          </div>

          {lookupError ? (
            <div className="text-xs text-red-600 dark:text-red-400">
              Failed to look up connection: {(lookupError as Error).message}
            </div>
          ) : lookupLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Looking up connection…
            </div>
          ) : !connection ? (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              No connection found for this store + platform. Nothing to pause.
            </div>
          ) : connection.pausedAt ? (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              This connection is already paused (since{' '}
              {new Date(connection.pausedAt).toLocaleString()}).
            </div>
          ) : (
            <>
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Reason</span>
                <select
                  value={reasonCode}
                  onChange={(e) => {
                    const next = e.target.value as PauseReasonCode;
                    setReasonCode(next);
                    if (!messageEdited) setMessage(DEFAULT_PAUSE_MESSAGES[next]);
                  }}
                  className="mt-1 block w-full rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100"
                >
                  {PAUSE_REASON_CODES.map((code) => (
                    <option key={code} value={code}>
                      {REASON_CODE_LABELS[code]}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Customer-facing message
                  {reasonCode === 'OTHER' && <span className="text-red-500"> *</span>}
                </span>
                <textarea
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    setMessageEdited(true);
                  }}
                  rows={3}
                  placeholder={
                    reasonCode === 'OTHER'
                      ? 'Required — what should the customer see?'
                      : 'Customer will see this on their Integrations page.'
                  }
                  className="mt-1 block w-full rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100"
                />
              </label>

              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Internal note (optional)
                </span>
                <textarea
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  rows={2}
                  placeholder="Goes to the audit log only, not shown to the customer."
                  className="mt-1 block w-full rounded-md border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 px-2 py-1.5 text-xs text-gray-900 dark:text-gray-100"
                />
              </label>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-950"
          >
            Cancel
          </button>
          <button
            onClick={() => pauseMutation.mutate()}
            disabled={
              lookupLoading ||
              !!lookupError ||
              !connection ||
              !!connection?.pausedAt ||
              pauseMutation.isPending
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {pauseMutation.isPending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <PauseCircle className="w-3 h-3" />
            )}
            Pause integration
          </button>
        </div>
      </div>
    </div>
  );
}
